import { useMemo, useState } from "react";
import { Loader2, Plus, UserRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/page-header";
import { Input } from "@/components/ui/input";
import { UsersTable } from "@/components/users-table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useTeams,
  useTenantUsers,
  useTenants,
  useUpsertUser,
} from "@/lib/queries";
import { useSessionContext } from "@/lib/session";
import {
  type TenantRole,
  type TenantUser,
} from "@nl/shared";
import { toast } from "sonner";

import { createFileRoute } from "@tanstack/react-router";
function ManagementUsersPage() {
  const { tenantId } = useSessionContext();
  const { data: tenants = [] } = useTenants();
  const { data: teams = [] } = useTeams();
  const { data: users = [] } = useTenantUsers();
  const saveUser = useUpsertUser();

  const sparkasse = tenants.find((tenant) => tenant.id === tenantId);

  const [editing, setEditing] = useState<TenantUser | null>(null);
  const teamOptions = useMemo(
    () => teams.map((t) => ({ id: t.id, name: t.name })),
    [teams],
  );

  // New users are created server-side and start in the "invited" state. The
  // invitation goes out over the magic link route.
  const upsertUser = (draft: TenantUser) => {
    saveUser.mutate(
      {
        id: draft.id || undefined,
        name: draft.name,
        email: draft.email,
        role: draft.role,
        status: draft.status,
      },
      {
        onSuccess: () => {
          toast.success(`${draft.name} gespeichert`);
          setEditing(null);
        },
        onError: () => toast.error("Speichern fehlgeschlagen."),
      },
    );
  };

  const newUser = (): TenantUser => ({
    id: "",
    tenantId: tenantId ?? undefined,
    name: "",
    email: "",
    role: "advisor",
    status: "invited",
  });

  return (
    <div className="space-y-7">
      <PageHeader
        eyebrow={
          <>
            <UserRound className="h-3.5 w-3.5" />
            {sparkasse?.name ?? "Sparkasse"} · Nutzer
          </>
        }
        title="Nutzerverwaltung"
        description="Rollen, Gruppen und Status der Nutzer dieser Sparkasse. Anmeldung erfolgt ausschließlich per SSO."
        actions={
          <Button onClick={() => setEditing(newUser())} className="gap-2">
            <Plus className="h-4 w-4" />
            Nutzer einladen
          </Button>
        }
      />

      <UsersTable
        users={users}
        teams={teamOptions}
        onEdit={setEditing}
        emptyLabel="Noch keine Nutzer in dieser Sparkasse."
        header={
          <div className="border-b border-[var(--rule)] bg-[var(--paper)]/45 px-5 py-4">
            <div className="eyebrow">Konten</div>
            <h2 className="mt-1 font-display text-[20px] font-medium tracking-tight">
              {users.length} Nutzer
            </h2>
          </div>
        }
      />

      {editing && (
        <UserEditor
          user={editing}
          teams={teamOptions}
          onClose={() => setEditing(null)}
          onSave={upsertUser}
          isSaving={saveUser.isPending}
        />
      )}
    </div>
  );
}

function UserEditor({
  user,
  teams,
  onClose,
  onSave,
  isSaving,
}: {
  user: TenantUser;
  teams: { id: string; name: string }[];
  onClose: () => void;
  onSave: (u: TenantUser) => void;
  isSaving?: boolean;
}) {
  const [draft, setDraft] = useState<TenantUser>(user);
  const set = <K extends keyof TenantUser>(k: K, v: TenantUser[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {user.name ? "Nutzer bearbeiten" : "Nutzer einladen"}
          </DialogTitle>
          <DialogDescription>
            Die Anmeldung erfolgt ausschließlich per SSO der Sparkasse. Rollen
            werden über SAML-/OIDC-Claims gemappt.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Name" full>
            <Input value={draft.name} onChange={(e) => set("name", e.target.value)} />
          </Field>
          <Field label="E-Mail" full>
            <Input
              type="email"
              value={draft.email}
              onChange={(e) => set("email", e.target.value)}
            />
          </Field>
          <Field label="Rolle">
            <Select
              value={draft.role}
              onValueChange={(v) => set("role", v as TenantRole)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="advisor">Berater</SelectItem>
                <SelectItem value="coach">Coach</SelectItem>
                <SelectItem value="manager">Führungskraft</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Gruppe">
            <div className="flex h-9 items-center text-[14px]">
              {teams.find((t) => t.id === draft.teamId)?.name ?? "Keine Gruppe"}
            </div>
            <p className="text-[12px] text-muted-foreground">
              Gruppen ordnet die neue leben Plattform zu.
            </p>
          </Field>
          <Field label="Status" full>
            <Select
              value={draft.status}
              onValueChange={(v) => set("status", v as TenantUser["status"])}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="invited">eingeladen</SelectItem>
                <SelectItem value="active">aktiv</SelectItem>
                <SelectItem value="disabled">deaktiviert</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button onClick={() => onSave(draft)} disabled={isSaving}>
            {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
            Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  children,
  full,
}: {
  label: string;
  children: React.ReactNode;
  full?: boolean;
}) {
  return (
    <div className={"space-y-1.5 " + (full ? "sm:col-span-2" : "")}>
      <Label className="text-[13px] uppercase tracking-wider text-muted-foreground">
        {label}
      </Label>
      {children}
    </div>
  );
}

export const Route = createFileRoute("/_app/management/users")({
  component: ManagementUsersPage,
});
